// Status-tab counts for the Risk Areas page. Deliberately outside Redux —
// these are one-row-per-bucket aggregates that would otherwise land in the
// same slices as the real list/header fetches and race them.
import { ALL_COUNT_KEY, RiskView, STATUS_COUNT_BUCKETS } from "../constants";
import { HeaderParams } from "../types";
import { fetchBillingHeaders } from "./billing-risks-service";
import { fetchProviderCoderHeaders } from "./provider-coder-risks-service";

// Counts every status tab for one view with a single `groupBy=Status` call off
// the header endpoints. Every groupBy value reads the identical filtered set, so
// this is the same rows the accordion's own Weight/Entity header calls see, just
// grouped by status — which is what keeps the tab badges and the grid in
// agreement.
//
// The entity view is one call, not two: /provider-coder/groups takes both
// providerGroupIds and coderIds and returns both groups together, rather than
// the client summing a separately-filtered /providers and /coders list.
export const fetchStatusCounts = async (
  view: RiskView,
  base: HeaderParams
): Promise<Record<string, number>> => {
  const params: HeaderParams = {
    ...base,
    // coderIds is a /provider-coder/groups-only filter — the billing header
    // request has no CoderIds property to bind it to.
    coderIds: view === "billing" ? undefined : base.coderIds,
    groupBy: "Status",
  };

  // Both header shapes carry groupValue + riskCount, the only fields read here.
  const rows: { groupValue: string; riskCount: number }[] =
    view === "billing"
      ? await fetchBillingHeaders(params)
      : await fetchProviderCoderHeaders(params);

  // Statuses arrive title-case, weight buckets lowercase elsewhere in the app —
  // compare folded so casing never desyncs a bucket from its groups.
  const countOf = (groupValues: string[]) =>
    rows
      .filter((row) =>
        groupValues.some(
          (value) => value.toLowerCase() === (row.groupValue ?? "").toLowerCase()
        )
      )
      .reduce((total, row) => total + (row.riskCount ?? 0), 0);

  const counts: Record<string, number> = {};
  STATUS_COUNT_BUCKETS.forEach((bucket) => {
    counts[`${bucket.key}-${view}`] = countOf(bucket.groupValues);
  });
  // Every group the response returns, not just the named buckets, so a
  // null/unexpected status is still counted in All.
  counts[`${ALL_COUNT_KEY}-${view}`] = rows.reduce(
    (total, row) => total + (row.riskCount ?? 0),
    0
  );
  return counts;
};
